import { useNavigate, useLocation } from 'react-router-dom';
import { Grid, Container, Center, Spinner } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { MeContext } from '../../context/MeContext';

import { usePlanToken } from '../../hooks/usePlanToken';
import BillingForm from '../../components/client/subscription/BillingForm';
import SubscriptionPlans from '../public/SubscriptionPlans';
import { useBillingService } from '../../services/billingService';
import PlanDetailsPanel from '../../components/client/subscription/PlanDetailsPanel';

const billingIntervalFromSearch = (search) => {
  const q = new URLSearchParams(search).get('interval');
  if (q === 'month') return 'month';
  return 'year';
};

const SubscriptionPage = () => {
  const meCtx = useContext(MeContext);
  const billingService = useBillingService();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    billingEnabled,
    cardlessTrialEnabled,
    cardlessTrialStartedAt,
    subscriptionRequired,
  } = meCtx.state;
  const { planToken } = usePlanToken();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingInterval, setSelectedBillingInterval] = useState(() =>
    billingIntervalFromSearch(location.search)
  );
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const envName =
    process.env.REACT_APP_ENV ||
    window.env?.REACT_APP_ENV ||
    window.env?.REACT_APP_NODE_ENV ||
    process.env.NODE_ENV;
  const isProduction = envName === 'production';
  const PILOT_PLAN_TOKEN = isProduction ? 'SqzcMBfjTUbwPj' : 'So0LaKgO4M1nMj';
  const isResolvingPlan = loading || (planToken && !selectedPlan);

  const handleSelectPlan = (plan) => {
    if (!isLoading) {
      setSelectedPlan(plan);
    }
  };

  const findMatchingPrice = (prices, interval = selectedBillingInterval) => {
    if (!prices?.length) return null;
    return prices.find((p) => p.interval === interval) || prices[0];
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('interval')) {
      setSelectedBillingInterval(billingIntervalFromSearch(location.search));
    }
  }, [location.search]);

  useEffect(() => {
    if (meCtx.state.subscribed) {
      navigate('/dashboard');
    }
  }, [meCtx.state.subscribed, navigate]);

  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      setLoading(true);
      try {
        const response = await billingService.getSubscriptionPlans();
        const list = Array.isArray(response)
          ? response
          : (response?.data ?? []);
        setPlans(list);
        const desiredToken = planToken || PILOT_PLAN_TOKEN;
        const findByToken = (token) =>
          list.find((plan) => plan.stripeProductId === `prod_${token}`);
        let matchingPlan =
          findByToken(desiredToken) || findByToken(PILOT_PLAN_TOKEN);
        if (!matchingPlan) {
          matchingPlan = list.find((plan) => plan.rank === 1) || list[0];
        }
        if (matchingPlan) {
          const selectedPrice =
            findMatchingPrice(matchingPlan.prices) ||
            matchingPlan?.prices?.[0] ||
            null;
          handleSelectPlan({
            ...matchingPlan,
            selectedPrice,
          });
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionPlans();
  }, [planToken]);

  useEffect(() => {
    if (selectedPlan?.prices && selectedBillingInterval) {
      const price = findMatchingPrice(selectedPlan.prices);
      if (price && price.id !== selectedPlan.selectedPrice?.id) {
        setSelectedPlan((prev) =>
          prev ? { ...prev, selectedPrice: price } : prev
        );
      }
    }
  }, [selectedBillingInterval, selectedPlan?.id]);

  return (
    <Grid
      templateColumns={['1fr', '1fr 1fr']}
      minH="100vh"
      h="100%"
      w="100vw"
      overflowY="auto"
    >
      <PlanDetailsPanel
        selectedPlan={selectedPlan}
        isResolvingPlan={isResolvingPlan}
        selectedBillingInterval={selectedBillingInterval}
        onBillingIntervalChange={setSelectedBillingInterval}
      />
      <Container
        bg="#F1F1F1"
        minH="100vh"
        h="100%"
        w={['100vw', '100vw', '100vw', '65vw']}
        px="5vw"
        py="5vh"
        overflow="scroll"
      >
        {isResolvingPlan ? (
          <Center h="100%">
            <Spinner size="lg" />
          </Center>
        ) : (
          <BillingForm
            selectedPlan={selectedPlan}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            companyName={meCtx?.state?.account?.company_name}
            accountEmail={meCtx?.state?.account?.email}
          />
        )}
      </Container>
    </Grid>
  );
};

export default SubscriptionPage;
